/**
 * Configurar o setor (mapa_producao:gerir):
 *
 * - ESTE SETOR: renomear a sala e escolher a ETAPA da planilha a que ela está
 *   ligada (é o vínculo que faz os ambientes daquela etapa chegarem aqui), mais
 *   a marca de quem RECEBE os ambientes quando vários setores apontam para a
 *   mesma etapa;
 * - SUBSETORES: criar, renomear, ordenar, desativar (o desenho deles é na
 *   visão Mapa);
 * - EXCLUIR (subsetor, colaborador, posto): só o que nunca carregou trabalho;
 *   o backend recusa o resto com a frase "desative em vez de excluir";
 * - COLABORADORES: com vínculo OPCIONAL a um nome da coluna MARCENEIRO
 *   RESPONSÁVEL da planilha;
 * - POSTOS de trabalho: um posto tem várias pessoas; uma pessoa pode estar em
 *   vários postos.
 *
 * Colaborador vinculado a um nome da planilha passa a aparecer como
 * responsável dos ambientes em que a planilha tem aquele marceneiro.
 */
import React, { useEffect, useId, useMemo, useState } from "react";
import { FiArrowDown, FiArrowUp } from "react-icons/fi";

import AsyncButton from "../../../components/AsyncButton";
import SlideOverPanel from "../../../components/SlideOverPanel";
import { plural } from "../../../utils/dinheiro";
import { gruposDeVinculo } from "../dados/etapasPlanilha";
import { chaveApelido } from "../dados/chaves";
import { limparEspacos } from "./formularios";
import { colaboradoresDoSetor, postosDoSetor, subsetoresDoSetor } from "./modelo";
import { Acoes, Ajuda, BotaoIcone, Campo, Campos, ItemLista, Lista, Marca, Marcas, Secao } from "./styles";
import { AvatarPessoa, ErroDaAcao, PillStatus, SeletorSegmentado, useErroDeAcao } from "./ui";

const TAMANHO_NOME = 120;

export const ABAS_CONFIGURACAO = Object.freeze([
  { valor: "setor", rotulo: "Este setor" },
  { valor: "subsetores", rotulo: "Subsetores" },
  { valor: "colaboradores", rotulo: "Colaboradores" },
  { valor: "postos", rotulo: "Postos de trabalho" },
]);

// ------------------------------------------------------------- este setor

/**
 * Opções do vínculo com a planilha: os mesmos grupos do formulário do mapa
 * (`gruposDeVinculo`), com as etapas paralelas separadas no fim.
 */
function OpcoesDeEtapa() {
  return gruposDeVinculo().map((grupo) => (
    <optgroup key={grupo.rotulo} label={grupo.rotulo}>
      {grupo.opcoes.map((opcao) => (
        <option key={opcao.valor} value={opcao.valor}>{opcao.texto}</option>
      ))}
    </optgroup>
  ));
}

function valoresDoSetor(setor) {
  return {
    nome: setor?.nome || "",
    etapa: setor?.etapa || "",
    recebe: Boolean(setor?.recebe_da_planilha),
    ativo: setor ? setor.ativo !== false : true,
  };
}

function AbaEsteSetor({ setor, catalogo, acoes, rodar }) {
  const [form, setForm] = useState(() => valoresDoSetor(setor));
  const versao = `${setor?.id}:${setor?.versao}`;
  const idAjuda = useId();
  useEffect(() => {
    setForm(valoresDoSetor(setor));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [versao]);

  const nome = limparEspacos(form.nome);
  const irmaos = catalogo.setoresDaEtapa(form.etapa).filter((s) => s.id !== setor?.id);
  const inicial = valoresDoSetor(setor);
  const mudou =
    nome !== inicial.nome ||
    form.etapa !== inicial.etapa ||
    form.recebe !== inicial.recebe ||
    form.ativo !== inicial.ativo;

  return (
    <Secao aria-label="Este setor">
      <h3>Nome e etapa da planilha</h3>
      <Campos $min={220}>
        <Campo>
          Nome do setor
          <input
            value={form.nome}
            maxLength={TAMANHO_NOME}
            placeholder="Ex.: Coladeira, Montagem, Acabamento"
            onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
            onBlur={() => setForm((f) => ({ ...f, nome: limparEspacos(f.nome) }))}
          />
        </Campo>
        <div>
          <Campo>
            Etapa da planilha
            <select
              value={form.etapa}
              aria-describedby={idAjuda}
              onChange={(e) => setForm((f) => ({ ...f, etapa: e.target.value }))}
            >
              <option value="">Sem etapa ligada</option>
              <OpcoesDeEtapa />
            </select>
          </Campo>
          <Ajuda id={idAjuda}>
            É por esta etapa que os ambientes chegam ao setor. Vários setores podem apontar para a mesma etapa;
            só um deles recebe os que chegam pela planilha.
          </Ajuda>
        </div>
      </Campos>
      <Marcas>
        <Marca $ativo={form.recebe}>
          <input
            type="checkbox"
            checked={form.recebe}
            onChange={(e) => setForm((f) => ({ ...f, recebe: e.target.checked }))}
          />
          Recebe os ambientes desta etapa
        </Marca>
        <Marca $ativo={form.ativo}>
          <input
            type="checkbox"
            checked={form.ativo}
            onChange={(e) => setForm((f) => ({ ...f, ativo: e.target.checked }))}
          />
          Setor ativo
        </Marca>
      </Marcas>
      {irmaos.length > 0 && (
        <p className="nota">
          Também nesta etapa: {irmaos.map((s) => s.nome).join(", ")}. Marcar este como quem recebe desmarca o
          outro; os ambientes continuam podendo ser movidos à mão para qualquer um deles.
        </p>
      )}
      {!form.etapa && (
        <p className="nota">
          Sem etapa ligada, nenhum ambiente chega sozinho a este setor: ele só recebe o que o gestor mover à mão.
        </p>
      )}
      <Acoes>
        <AsyncButton
          silentError
          disabled={!nome || !mudou}
          successMessage="Setor atualizado."
          onClick={() =>
            rodar(() =>
              acoes.salvarSetor(setor.id, {
                nome,
                etapa: form.etapa || null,
                recebe_da_planilha: form.recebe,
                ativo: form.ativo,
                versao: setor.versao,
              })
            )
          }
        >
          Salvar setor
        </AsyncButton>
      </Acoes>
    </Secao>
  );
}

// ------------------------------------------------------------- subsetores

/**
 * Excluir em dois toques: o primeiro só pergunta. Apagar não tem desfazer, e
 * o botão fica ao lado de "Desativar" — um toque errado no tablet não pode
 * sumir com o cadastro.
 */
function ExcluirComConfirmacao({ nome, sucesso, onExcluir }) {
  const [confirmando, setConfirmando] = useState(false);
  if (!confirmando) {
    return (
      <button type="button" className="btn btn--ghost btn--sm" onClick={() => setConfirmando(true)}>
        Excluir
      </button>
    );
  }
  return (
    <>
      <AsyncButton
        variant="danger"
        silentError
        successMessage={sucesso}
        aria-label={`Confirmar exclusão de ${nome}`}
        onClick={async () => {
          try {
            await onExcluir();
          } finally {
            setConfirmando(false);
          }
        }}
      >
        Excluir de vez
      </AsyncButton>
      <button type="button" className="btn btn--ghost btn--sm" onClick={() => setConfirmando(false)}>
        Não
      </button>
    </>
  );
}

function LinhaSubsetor({ subsetor, primeiro, ultimo, reordenando, onMover, acoes, rodar }) {
  const [editando, setEditando] = useState(false);
  const [nome, setNome] = useState(subsetor.nome);
  useEffect(() => setNome(subsetor.nome), [subsetor.nome]);
  const inativo = subsetor.ativo === false;
  return (
    <ItemLista data-inativo={inativo ? "sim" : undefined}>
      <BotaoIcone
        type="button"
        disabled={primeiro || reordenando}
        aria-busy={reordenando || undefined}
        onClick={() => onMover(-1)}
        aria-label={`Subir ${subsetor.nome}`}
      >
        <FiArrowUp size={14} aria-hidden="true" />
      </BotaoIcone>
      <BotaoIcone
        type="button"
        disabled={ultimo || reordenando}
        aria-busy={reordenando || undefined}
        onClick={() => onMover(1)}
        aria-label={`Descer ${subsetor.nome}`}
      >
        <FiArrowDown size={14} aria-hidden="true" />
      </BotaoIcone>
      <span className="principal">
        {editando ? (
          <Campo>
            <input
              value={nome}
              maxLength={TAMANHO_NOME}
              aria-label={`Novo nome de ${subsetor.nome}`}
              onChange={(e) => setNome(e.target.value)}
              onBlur={() => setNome((n) => limparEspacos(n))}
            />
          </Campo>
        ) : (
          <span className="l1">{subsetor.nome}</span>
        )}
      </span>
      {inativo && <PillStatus ponto={false}>Desativado</PillStatus>}
      {editando ? (
        <>
          <AsyncButton
            silentError
            disabled={!limparEspacos(nome) || limparEspacos(nome) === subsetor.nome}
            successMessage="Subsetor renomeado."
            onClick={() =>
              rodar(async () => {
                await acoes.salvarSubsetor(subsetor.id, { nome: limparEspacos(nome), versao: subsetor.versao });
                setEditando(false);
              })
            }
          >
            Salvar
          </AsyncButton>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setEditando(false)}>
            Cancelar
          </button>
        </>
      ) : (
        <>
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => setEditando(true)}>
            Renomear
          </button>
          <AsyncButton
            variant="secondary"
            silentError
            successMessage={inativo ? "Subsetor reativado." : "Subsetor desativado."}
            onClick={() => rodar(() => acoes.salvarSubsetor(subsetor.id, { ativo: inativo, versao: subsetor.versao }))}
          >
            {inativo ? "Reativar" : "Desativar"}
          </AsyncButton>
          <ExcluirComConfirmacao
            nome={subsetor.nome}
            sucesso="Subsetor excluído."
            onExcluir={() => rodar(() => acoes.excluirCadastro("subsetor", subsetor))}
          />
        </>
      )}
    </ItemLista>
  );
}

function AbaSubsetores({ setorId, setorNome, estado, acoes, rodar, onRecarregar }) {
  const [nome, setNome] = useState("");
  const [reordenando, setReordenando] = useState(false);
  const subsetores = subsetoresDoSetor(estado?.subsetores, setorId, { incluirInativos: true });

  // Reordenar grava a posição de todos os que mudaram (ordem = 1, 2, 3…).
  // As setas ficam travadas até o /estado voltar com a ordem nova: um
  // segundo toque com a lista velha gravaria posições trocadas. O erro já
  // fica no painel (rodar); o catch só evita a rejeição solta.
  const mover = async (indice, direcao) => {
    if (reordenando) return;
    setReordenando(true);
    try {
      await rodar(async () => {
        const lista = [...subsetores];
        const [item] = lista.splice(indice, 1);
        lista.splice(indice + direcao, 0, item);
        const mudaram = lista.map((s, i) => ({ s, ordem: i + 1 })).filter(({ s, ordem }) => s.ordem !== ordem);
        for (const { s, ordem } of mudaram) {
          // eslint-disable-next-line no-await-in-loop
          await acoes.salvarSubsetor(s.id, { ordem, versao: s.versao });
        }
        await onRecarregar?.();
      });
    } catch {
      // Mensagem já exibida por ErroDaAcao.
    } finally {
      setReordenando(false);
    }
  };

  const proximaOrdem = subsetores.reduce((maior, s) => Math.max(maior, s.ordem ?? 0), 0) + 1;

  return (
    <>
      <Secao aria-label="Novo subsetor">
        <h3>Novo subsetor</h3>
        <Campos $min={220}>
          <Campo>
            Nome
            <input
              value={nome}
              maxLength={TAMANHO_NOME}
              placeholder="Ex.: Serra 1, Montagem, Embalagem"
              onChange={(e) => setNome(e.target.value)}
              onBlur={() => setNome((n) => limparEspacos(n))}
            />
          </Campo>
        </Campos>
        <Ajuda>
          Criado aqui, o subsetor nasce sem desenho e aparece numa caixa dentro da sala. Use a visão Mapa para
          desenhar a área dele à mão.
        </Ajuda>
        <Acoes>
          <AsyncButton
            silentError
            disabled={!limparEspacos(nome)}
            successMessage="Subsetor criado."
            onClick={() =>
              rodar(async () => {
                await acoes.salvarSubsetor(null, {
                  setor_id: setorId,
                  nome: limparEspacos(nome),
                  ordem: proximaOrdem,
                });
                setNome("");
              })
            }
          >
            Adicionar subsetor
          </AsyncButton>
        </Acoes>
      </Secao>
      <Secao aria-label={`Subsetores de ${setorNome}`}>
        <h3>Subsetores de {setorNome}</h3>
        {subsetores.length === 0 ? (
          <p className="nota">Nenhum subsetor ainda. Sem subsetor, tudo fica na fila do setor.</p>
        ) : (
          <Lista>
            {subsetores.map((s, i) => (
              <LinhaSubsetor
                key={s.id}
                subsetor={s}
                primeiro={i === 0}
                ultimo={i === subsetores.length - 1}
                reordenando={reordenando}
                onMover={(direcao) => mover(i, direcao)}
                acoes={acoes}
                rodar={rodar}
              />
            ))}
          </Lista>
        )}
      </Secao>
    </>
  );
}

// ---------------------------------------------------------- colaboradores

function textoDoApelido(apelido) {
  const nome = apelido.tipo ? `${apelido.nome} (${apelido.tipo})` : apelido.nome;
  return `${nome} · ${plural(apelido.total, "ambiente", "ambientes")}`;
}

function OpcoesDeApelido({ apelidos, colaboradores, editandoId, valorAtual }) {
  const donoPorChave = new Map(
    (colaboradores || [])
      .filter((c) => c.apelido_planilha && c.id !== editandoId)
      .map((c) => [chaveApelido(c.apelido_planilha), c])
  );
  const naLista = apelidos.some((a) => chaveApelido(a.apelido) === chaveApelido(valorAtual));
  return (
    <>
      <option value="">Sem vínculo com a planilha</option>
      {valorAtual && !naLista && <option value={valorAtual}>{valorAtual} (não aparece na planilha agora)</option>}
      {apelidos.map((a) => {
        const dono = donoPorChave.get(chaveApelido(a.apelido));
        return (
          <option key={a.apelido} value={a.apelido} disabled={Boolean(dono)}>
            {textoDoApelido(a)}
            {dono ? ` · já é de ${dono.nome}` : ""}
          </option>
        );
      })}
    </>
  );
}

function valoresDoColaborador(colaborador, setorId) {
  return {
    nome: colaborador?.nome || "",
    apelido: colaborador?.apelido_planilha || "",
    setores: colaborador ? [...(colaborador.setor_ids || [])] : [setorId],
    ativo: colaborador ? colaborador.ativo !== false : true,
  };
}

function FormColaborador({ colaborador, setorId, catalogo, apelidos, colaboradores, acoes, rodar, onPronto }) {
  const [form, setForm] = useState(() => valoresDoColaborador(colaborador, setorId));
  const quem = colaborador?.id || "novo";
  useEffect(() => {
    // Recomeça ao trocar de pessoa, não a cada leitura do estado (15 s).
    setForm(valoresDoColaborador(colaborador, setorId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quem, setorId]);
  const apelidoPorValor = useMemo(() => new Map(apelidos.map((a) => [a.apelido, a])), [apelidos]);

  const escolherApelido = (valor) =>
    setForm((f) => ({
      ...f,
      apelido: valor,
      // Nome em branco: sugere o da planilha ("NALDO (EMPREITA)" vira "Naldo").
      nome: f.nome.trim() ? f.nome : apelidoPorValor.get(valor)?.nome || f.nome,
    }));
  const alternarSetor = (id) =>
    setForm((f) => ({
      ...f,
      setores: f.setores.includes(id) ? f.setores.filter((s) => s !== id) : [...f.setores, id],
    }));

  // O gravado pode ter outra grafia ("Naldo (empreita)"): o select mostra a
  // opção da planilha que casa na forma normalizada.
  const idAjuda = useId();
  const opcaoIgual = apelidos.find((a) => chaveApelido(a.apelido) === chaveApelido(form.apelido));
  const valorDoSelect = opcaoIgual ? opcaoIgual.apelido : form.apelido;

  const nome = limparEspacos(form.nome);
  const salvar = () =>
    rodar(async () => {
      const dados = {
        nome,
        apelido_planilha: form.apelido.trim(),
        // Na ordem do mapa, e só ids que existem no cadastro.
        setor_ids: catalogo.todos.map((s) => s.id).filter((id) => form.setores.includes(id)),
        ativo: form.ativo,
      };
      await acoes.salvarColaborador(colaborador?.id || null, dados);
      if (!colaborador) setForm(valoresDoColaborador(null, setorId));
      onPronto?.();
    });

  return (
    <Secao aria-label={colaborador ? "Editar colaborador" : "Novo colaborador"}>
      <h3>{colaborador ? `Editar ${colaborador.nome}` : "Novo colaborador"}</h3>
      <Campos $min={220}>
        <Campo>
          Nome
          <input
            value={form.nome}
            maxLength={TAMANHO_NOME}
            onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
            onBlur={() => setForm((f) => ({ ...f, nome: limparEspacos(f.nome) }))}
          />
        </Campo>
        <div>
          <Campo>
            Nome na planilha (opcional)
            <select
              value={valorDoSelect}
              onChange={(e) => escolherApelido(e.target.value)}
              aria-describedby={idAjuda}
            >
              <OpcoesDeApelido
                apelidos={apelidos}
                colaboradores={colaboradores}
                editandoId={colaborador?.id}
                valorAtual={valorDoSelect}
              />
            </select>
          </Campo>
          <Ajuda id={idAjuda}>Da coluna Marceneiro responsável. Liga a pessoa aos ambientes dela.</Ajuda>
        </div>
      </Campos>
      <Campo as="div">
        Setores
        <Marcas role="group" aria-label="Setores do colaborador">
          {catalogo.setores.map((s) => (
            <Marca key={s.id} $ativo={form.setores.includes(s.id)}>
              <input
                type="checkbox"
                checked={form.setores.includes(s.id)}
                onChange={() => alternarSetor(s.id)}
              />
              {s.nome}
            </Marca>
          ))}
        </Marcas>
      </Campo>
      {colaborador && (
        <Marcas>
          <Marca $ativo={form.ativo}>
            <input type="checkbox" checked={form.ativo} onChange={(e) => setForm((f) => ({ ...f, ativo: e.target.checked }))} />
            Ativo
          </Marca>
        </Marcas>
      )}
      <Acoes>
        <AsyncButton
          silentError
          disabled={!nome}
          successMessage={colaborador ? "Colaborador atualizado." : "Colaborador cadastrado."}
          onClick={salvar}
        >
          {colaborador ? "Salvar colaborador" : "Cadastrar colaborador"}
        </AsyncButton>
        {colaborador && (
          <button type="button" className="btn btn--secondary" onClick={onPronto}>
            Cancelar
          </button>
        )}
      </Acoes>
    </Secao>
  );
}

function AbaColaboradores({ setorId, setorNome, catalogo, estado, apelidos, acoes, rodar }) {
  const [editando, setEditando] = useState(null);
  const [soDoSetor, setSoDoSetor] = useState(true);
  const colaboradores = estado?.colaboradores || [];
  const lista = (soDoSetor
    ? colaboradores.filter((c) => (c.setor_ids || []).includes(setorId))
    : [...colaboradores]
  ).sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  const emEdicao = editando ? colaboradores.find((c) => c.id === editando) || null : null;

  return (
    <>
      <FormColaborador
        colaborador={emEdicao}
        setorId={setorId}
        catalogo={catalogo}
        apelidos={apelidos}
        colaboradores={colaboradores}
        acoes={acoes}
        rodar={rodar}
        onPronto={() => setEditando(null)}
      />
      <Secao aria-label="Colaboradores cadastrados">
        <h3>Colaboradores</h3>
        <SeletorSegmentado
          rotulo="Quais colaboradores"
          opcoes={[
            { valor: "setor", rotulo: `De ${setorNome}`, n: colaboradoresDoSetor(colaboradores, setorId).length },
            { valor: "todos", rotulo: "Todos", n: colaboradores.length },
          ]}
          valor={soDoSetor ? "setor" : "todos"}
          onChange={(v) => setSoDoSetor(v === "setor")}
        />
        {lista.length === 0 ? (
          <p className="nota">Nenhum colaborador aqui ainda.</p>
        ) : (
          <Lista>
            {lista.map((c) => (
              <ItemLista key={c.id} data-inativo={c.ativo === false ? "sim" : undefined}>
                <AvatarPessoa nome={c.nome} tamanho={28} />
                <span className="principal">
                  <span className="l1">{c.nome}</span>
                  <span className="l2">
                    {c.apelido_planilha ? `Planilha: ${c.apelido_planilha}` : "Sem vínculo com a planilha"}
                    {" · "}
                    {(c.setor_ids || []).map((id) => catalogo.nome(id)).filter(Boolean).join(", ") || "Sem setor"}
                  </span>
                </span>
                {c.ativo === false && <PillStatus ponto={false}>Desativado</PillStatus>}
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => setEditando(c.id)}>
                  Editar
                </button>
                <ExcluirComConfirmacao
                  nome={c.nome}
                  sucesso="Colaborador excluído."
                  onExcluir={() => rodar(() => acoes.excluirCadastro("colaborador", c))}
                />
              </ItemLista>
            ))}
          </Lista>
        )}
      </Secao>
    </>
  );
}

// ----------------------------------------------------------------- postos

function valoresDoPosto(posto) {
  return {
    nome: posto?.nome || "",
    subsetorId: posto?.subsetor_id || "",
    colaboradorIds: posto ? [...(posto.colaborador_ids || [])] : [],
    ativo: posto ? posto.ativo !== false : true,
  };
}

function FormPosto({ posto, setorId, estado, acoes, rodar, onPronto }) {
  const [form, setForm] = useState(() => valoresDoPosto(posto));
  const qual = posto?.id || "novo";
  useEffect(() => {
    setForm(valoresDoPosto(posto));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qual]);
  const subsetores = subsetoresDoSetor(estado?.subsetores, setorId, { incluirInativos: true }).filter(
    (s) => s.ativo !== false || s.id === form.subsetorId
  );
  const colaboradores = estado?.colaboradores || [];
  const doSetor = colaboradoresDoSetor(colaboradores, setorId);
  const idsDoSetor = new Set(doSetor.map((c) => c.id));
  const outros = colaboradores
    .filter((c) => !idsDoSetor.has(c.id) && (c.ativo !== false || form.colaboradorIds.includes(c.id)))
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));
  const alternar = (id) =>
    setForm((f) => ({
      ...f,
      colaboradorIds: f.colaboradorIds.includes(id) ? f.colaboradorIds.filter((x) => x !== id) : [...f.colaboradorIds, id],
    }));

  const nome = limparEspacos(form.nome);
  const salvar = () =>
    rodar(async () => {
      const dados = {
        nome,
        subsetor_id: form.subsetorId || null,
        colaborador_ids: form.colaboradorIds,
        ...(posto ? { ativo: form.ativo, versao: posto.versao } : { setor_id: setorId }),
      };
      await acoes.salvarPosto(posto?.id || null, dados);
      if (!posto) setForm(valoresDoPosto(null));
      onPronto?.();
    });

  const marca = (c) => (
    <Marca key={c.id} $ativo={form.colaboradorIds.includes(c.id)}>
      <input type="checkbox" checked={form.colaboradorIds.includes(c.id)} onChange={() => alternar(c.id)} />
      {c.nome}
    </Marca>
  );

  return (
    <Secao aria-label={posto ? "Editar posto" : "Novo posto"}>
      <h3>{posto ? `Editar ${posto.nome}` : "Novo posto de trabalho"}</h3>
      <Campos $min={220}>
        <Campo>
          Nome
          <input
            value={form.nome}
            maxLength={TAMANHO_NOME}
            placeholder="Ex.: Bancada 1, Seccionadora"
            onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
            onBlur={() => setForm((f) => ({ ...f, nome: limparEspacos(f.nome) }))}
          />
        </Campo>
        <Campo>
          Subsetor
          <select value={form.subsetorId} onChange={(e) => setForm((f) => ({ ...f, subsetorId: e.target.value }))}>
            <option value="">Sem subsetor (fila do setor)</option>
            {subsetores.map((s) => (
              <option key={s.id} value={s.id}>
                {s.nome}
                {s.ativo === false ? " (desativado)" : ""}
              </option>
            ))}
          </select>
        </Campo>
      </Campos>
      <Campo as="div">
        Quem trabalha neste posto
        {colaboradores.length === 0 ? (
          <span className="ajuda">Cadastre colaboradores na aba Colaboradores.</span>
        ) : (
          <>
            <Marcas role="group" aria-label="Colaboradores do posto">
              {doSetor.map(marca)}
            </Marcas>
            {outros.length > 0 && (
              <>
                <span className="ajuda">De outros setores</span>
                <Marcas role="group" aria-label="Colaboradores de outros setores">
                  {outros.map(marca)}
                </Marcas>
              </>
            )}
          </>
        )}
      </Campo>
      {posto && (
        <Marcas>
          <Marca $ativo={form.ativo}>
            <input type="checkbox" checked={form.ativo} onChange={(e) => setForm((f) => ({ ...f, ativo: e.target.checked }))} />
            Ativo
          </Marca>
        </Marcas>
      )}
      <Acoes>
        <AsyncButton
          silentError
          disabled={!nome}
          successMessage={posto ? "Posto atualizado." : "Posto criado."}
          onClick={salvar}
        >
          {posto ? "Salvar posto" : "Criar posto"}
        </AsyncButton>
        {posto && (
          <button type="button" className="btn btn--secondary" onClick={onPronto}>
            Cancelar
          </button>
        )}
      </Acoes>
    </Secao>
  );
}

function AbaPostos({ setorId, setorNome, estado, acoes, rodar }) {
  const [editando, setEditando] = useState(null);
  const postos = postosDoSetor(estado?.postos, setorId, { incluirInativos: true });
  const nomes = new Map((estado?.colaboradores || []).map((c) => [c.id, c.nome]));
  const subsetores = new Map((estado?.subsetores || []).map((s) => [s.id, s.nome]));
  const emEdicao = editando ? postos.find((p) => p.id === editando) || null : null;

  return (
    <>
      <FormPosto
        posto={emEdicao}
        setorId={setorId}
        estado={estado}
        acoes={acoes}
        rodar={rodar}
        onPronto={() => setEditando(null)}
      />
      <Secao aria-label={`Postos de ${setorNome}`}>
        <h3>Postos de {setorNome}</h3>
        {postos.length === 0 ? (
          <p className="nota">Nenhum posto ainda.</p>
        ) : (
          <Lista>
            {postos.map((p) => {
              const pessoas = (p.colaborador_ids || []).map((id) => nomes.get(id)).filter(Boolean);
              return (
                <ItemLista key={p.id} data-inativo={p.ativo === false ? "sim" : undefined}>
                  <span className="principal">
                    <span className="l1">{p.nome}</span>
                    <span className="l2">
                      {subsetores.get(p.subsetor_id) || "Fila do setor"}
                      {" · "}
                      {pessoas.length ? pessoas.join(", ") : "Sem ninguém"}
                    </span>
                  </span>
                  {p.ativo === false && <PillStatus ponto={false}>Desativado</PillStatus>}
                  <button type="button" className="btn btn--ghost btn--sm" onClick={() => setEditando(p.id)}>
                    Editar
                  </button>
                  <ExcluirComConfirmacao
                    nome={p.nome}
                    sucesso="Posto excluído."
                    onExcluir={() => rodar(() => acoes.excluirCadastro("posto", p))}
                  />
                </ItemLista>
              );
            })}
          </Lista>
        )}
      </Secao>
    </>
  );
}

/**
 * Props: aberto, aba, onMudarAba, setor (do catálogo), catalogo, estado,
 * apelidos (apelidosDaPlanilha), acoes, onFechar, onRecarregar.
 */
export default function ConfigurarSetor({
  aberto,
  aba,
  onMudarAba,
  setor,
  catalogo,
  estado,
  apelidos,
  acoes,
  onFechar,
  onRecarregar,
}) {
  const { erro, rodar, limpar } = useErroDeAcao();
  useEffect(() => {
    limpar();
  }, [aba, aberto, limpar]);
  const setorId = setor?.id || null;
  const setorNome = setor?.nome || "";
  const props = { setorId, setorNome, estado, acoes, rodar };

  return (
    <SlideOverPanel
      open={aberto}
      onClose={onFechar}
      title={`Configurar ${setorNome}`}
      subtitle="Nome e etapa, subsetores, colaboradores e postos de trabalho"
      width="min(96vw, 760px)"
    >
      <Secao>
        <SeletorSegmentado rotulo="Configuração" opcoes={ABAS_CONFIGURACAO} valor={aba} onChange={onMudarAba} />
      </Secao>
      <ErroDaAcao erro={erro} onRecarregar={onRecarregar} />
      {aba === "setor" && <AbaEsteSetor setor={setor} catalogo={catalogo} acoes={acoes} rodar={rodar} />}
      {aba === "subsetores" && <AbaSubsetores {...props} onRecarregar={onRecarregar} />}
      {aba === "colaboradores" && <AbaColaboradores {...props} catalogo={catalogo} apelidos={apelidos} />}
      {aba === "postos" && <AbaPostos {...props} />}
    </SlideOverPanel>
  );
}
